// src/com/tsr/TransPlanTsrItem.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, Chip, Grid2 } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person'; // 차량기사
import SystemSecurityUpdateGoodIcon from '@mui/icons-material/SystemSecurityUpdateGood'; // mobile
import StorefrontIcon from '@mui/icons-material/Storefront'; // 동적표시용.
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { AppObject, AppStyle, AppWord, MapValue, TopCode } from '../../value/AppConst';
import {
    authTableTreeRepo, mapRequestRepo, vehInfoListOfDcRepo
} from '../../value/AppModel';
import IsValid from '../../value/IsValid';
import { NoDataBox } from '../../common/TopStyle';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import { GET_TRANS_PLAN_TSR, ISSUE_TRANS_PLAN_TSR, SAVE_TRANS_PLAN_TSR } from './TransPlanTsrGql';
import { SEND_SMS_TRANS_PLAN_FOR_DEPART, SEND_SMS_TRANS_PLAN_FOR_READY } from '../trans_plan/TransPlanGql';
import { getVehicleObject } from '../trans_plan_launch/TransPlanLaunchItem';
import TransPlanTsrEditorGroup from './TransPlanTsrEditorGroup';
import ValueMisc from '../../value/ValueMisc';

const LabelSize = 3;
const ValueSize = 9;

const LGrid = styled(Grid2)({
    fontSize: '0.75rem', color: AppStyle.PrimaryRGB, borderBottom: '1px dotted  #bdf',
});

const VGrid = styled(Grid2)({
    borderBottom: '1px dotted #bdf',
});

const LabelBox = styled(Box)({
    flexGrow:1, textAlign:'center', padding: '0.5rem', display: 'flex', alignItems:'center',
    justifyContent: 'center', 
});

const Pair = ({title, value, style}) => {
    return(
        <>
        <LGrid item size={LabelSize} style={style}>{title}</LGrid>
        <VGrid item size={ValueSize} style={style}>{value}</VGrid>
        </>
    )
};

const getHhmm = (value) => {
    return value ? value.substring(11,16) : null
};

export default function TransPlanTsrItem({
    item, // item of LIST_TRANS_PLAN_TSR. {hocha, planId, dcCd, dcName, storeId, storeName,... bulkItems:{}}
    onCompleteMutation,
    onRequestViewDetail,
}) {
    // const menuTree = useReactiveVar(menuTreeRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const vehInfoListOfDcData = useReactiveVar(vehInfoListOfDcRepo);
    const [vehFound, setVehFound] = useState(null);
    const [editorState, setEditorState] = useState({open: false, tab: 0, data: null, changed: false});
    const [sentSmsToReady, setSentSmsToReady] = useState(false);
    const [goodMobile, setGoodMobile] = useState(false);
    const [daysOld, setDaysOld] = useState(0);
    const [storesBound, setStoresBound] = useState(false);
    // const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const checkAuth = useAuthorizationCheck();
    const [getTransPlanTsrItem, responseItem] = useLazyQuery(GET_TRANS_PLAN_TSR, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetTransPlanTsrItem(data, option)},
		onError: (error) => addAlert({title: '배차정보 조회 오류', message: [error.message]})
	});
    const [saveTransPlanTsr, responseSaveTsr] = useMutation( SAVE_TRANS_PLAN_TSR, {
        onCompleted: (data, option) => onCompleteSaveTsr(data, option),
        onError: (error) => addAlert({title: "배차정보 저장 오류", message: [error.message]})
    } );
    const [issueTransPlanTsr, responseIssueTsr] = useMutation(ISSUE_TRANS_PLAN_TSR, {
        onCompleted: (data, option) => onCompleteIssueTsr(data, option),
        onError: (error) => addAlert({title: 'TSR 발행 오류', message: [error.message]})
    });
    const [sendSmsForReady, responseSendSms] = useMutation( SEND_SMS_TRANS_PLAN_FOR_READY, {
		onCompleted: (data, option) => onCompleteSendSmsForReady(data, option), 
		onError: (error) => addAlert({title: '출차준비 메시지 전송 오류', message: [error.message]})
	} );
    const [sendSmsForDeparture, responseSendSmsForDeparture] = useMutation( SEND_SMS_TRANS_PLAN_FOR_DEPART, {
		onCompleted: (data, option) => onCompleteSendSmsForDeparture(data, option), 
		onError: (error) => addAlert({title: '출차메시지 전송 오류', message: [error.message]})
	} );

    useEffect(()=>{
        setEditorState({open: false, tab: 0, data: null, changed: false});
        const isGood = IsValid.mobile(item.driverMobile);
        setGoodMobile(isGood);
        const planDate = new Date(item.planDate);
        const today = new Date();
        setDaysOld(Math.floor((today - planDate) / 86400000));
        setStoresBound(item.bindStores && item.bindStores.length > 0);
        if(!isGood) setSentSmsToReady(true); // 전화번호가 잘못된 경우는 어차피 보낼 수 없으므로 true로 설정.
    }, [item]);

    useEffect(() => {
        if(item && vehInfoListOfDcData && vehInfoListOfDcData.records && item.gpsTime) {
            const veh = getVehicleObject(vehInfoListOfDcData.records[item.comId], item.comId, item.vehPlates);
            setVehFound(veh);
        }
    }, [vehInfoListOfDcData, item]);

    const onCompleteGetTransPlanTsrItem = (data, option) => {
        if(data.transPlanTsrItem) {
            setEditorState({open:true, data:data.transPlanTsrItem, changed: false,});
        }
        else addAlert({title:'배차정보 조회 오류', message:['배차정보를 찾을 수 없습니다.']});
    };

    const onCompleteSaveTsr = (data, clientOption) => {
        if(data.transPlanTsrSave.ok) {
            const newSate = {...editorState, changed: false, data: null};
            setEditorState(newSate);
            enqueueSnackbar('변경사항을 저장했습니다.', {variant: 'success', autoHideDuration: 1600});
            onCompleteMutation();
        }
        else addAlert({title:"배차정보 저장 오류", message:[data.transPlanTsrSave.message]});
    };

    const onCompleteIssueTsr = (data, clientOption) => {
        if(data.transPlanTsrIssue.ok) {
            enqueueSnackbar('TSR을 발행했습니다.', {variant: 'success', autoHideDuration: 1600});
            popTsrPrintWindow();
            onCompleteMutation();
        }
        else addAlert({title:'TSR 발행 오류', message:[data.transPlanTsrIssue.message]});
    };

    const onCompleteSendSmsForReady = (data, clientOption) => {
        if(data.transPlanSendSmsForReady.ok) {
            enqueueSnackbar('출차 준비 메시지를 전송했습니다.', {variant: 'success', autoHideDuration: 1600});
            setSentSmsToReady(true);
        }
        else addAlert({title:'출차메시지 전송 오류', message:[data.transPlanSendSmsForReady.message]});
    };

    const onCompleteSendSmsForDeparture = (data, clientOption) => {
        if(data.transPlanSendSmsForDepart.ok) {
            enqueueSnackbar('출차 메시지를 전송했습니다.', {variant: 'success', autoHideDuration: 1600});
        }
        else addAlert({title:'출차메시지 전송 오류', message:[data.transPlanSendSmsForDepart.message]});
    };

    const getTransPlanTsrItemOrOpenEditor = () => {
        if(editorState.data) {
            // open editor
            setEditorState({...editorState, open: true});
        }
        else {
            // get data
            getTransPlanTsrItem({variables: {planId: item.planId}});
        }
    };

    const onRequestCloseGroupEditor = ()=> {
        const newState = {...editorState, open: false};
        setEditorState(newState);
    }

    const onClickGoToMap = () => {
        if(item.gpsTime && vehFound) {
            // 지도로 이동
            if(vehFound.gpsTime) {
                const mapData = {
                    job: MapValue.Job.ShowTruck,
                    info: {
                        vehInfoSeq:vehFound.vehInfoSeq,
                        vehPlates: vehFound.vehPlates,
                        driverName: vehFound.driverName,
                        gpsTime: vehFound.gpsTime,
                        lat: vehFound.lat,
                        lon: vehFound.lon,
                        location: vehFound.location,
                        posSys: vehFound.posSys,
                        direction: vehFound.direction,
                        speed: vehFound.speed,
                    }
                };
                mapRequestRepo({data: mapData});
            }
            else {
                addAlert({title:'차량 위치 정보 없음', message:['차량의 위치 정보가 없습니다.']});
            }
        }
    };

    const onClickSendReadyMsg = () => {
        const confirm = window.confirm(`${item.hocha}호차의 출차 준비 메시지를 전송하시겠습니까?`);
        if(confirm) sendSmsForReady({variables:{planId:item.planId}});
        else setSentSmsToReady(true); // 실제는 보내지 않았으나 거부한 경우도 보낸 것으로 처리.
    };

    // 출차 메시지 전송. TSR 발행 후에만 가능.
    const onClickSendMessageForDeparture = () => {
        const confirm = window.confirm(`${item.hocha}호차의 출차 메시지를 전송하시겠습니까?`);
        if(confirm) sendSmsForDeparture({variables:{planId:item.planId}});
    };

    const onClickSetDetail = () => {
        getTransPlanTsrItemOrOpenEditor();
    };

    const onRequestSaveTsr = (data) => {
        saveTransPlanTsr({variables:{transPlanTsr:data}});
    };

    const popTsrPrintWindow = () => {
        const url = ValueMisc.makeTsrUrl({planId: item.planId});
        window.open(url, AppWord.TSR_PRINT_WINDOW_NAME);
    };

    /**
     * TSR 저장 && TSR 미발행이면 TSR 발행한 후 출력하고
     * TSR 발행이면 TSR 출력만 한다.
     */
    const onClickPrintTsr = () => {
        if(item.reqStateFlag === TopCode.ReqStateFlag.TsrNotIssued) {
            // == '1'
            issueTransPlanTsr({variables:{planId:item.planId}});
        }
        else if(item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued) {
            // == '2'
            popTsrPrintWindow();
        }
    };

    const okToSendReadyMsg = checkAuth(authTableTree.tree.pr_trans_plan_sms_for_ready);
    const okToSendLeaveMsg = checkAuth(authTableTree.tree.pr_trans_plan_sms_to_go);
    const canSendReadyMsg
    = okToSendReadyMsg && item.dcUseYn==='Y'
    && item.reqStateFlag !== TopCode.ReqStateFlag.TsrIssued
    && item.transFlag;
    const canPrintTsr
    = (sentSmsToReady || Boolean(item.loadStTime) || item.reqStateFlag !== TopCode.ReqStateFlag.NewRequest)
    && item.transFlag; // transFlg : 배차 여부.
    // 출차 메시지
    const canSendMessageToLeave
    = (sentSmsToReady || Boolean(item.loadStTime) || item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued)
    && okToSendLeaveMsg
    && goodMobile
    && item.transFlag;

    const loadingAny
    = responseSendSms.loading
    || responseSendSmsForDeparture.loading
    || responseItem.loading
    || responseSaveTsr.loading;

    const renderBindStores = () => {
        const style= {
            padding:'0.4rem', display:'flex', alignItems:'center',
            overflow: 'hidden', textOverflow: 'ellipsis',
            backgroundColor: '#eee', borderRadius: '0.5rem',
        };
        // 
        if(storesBound && item && item.bindStores && item.bindStores.length > 0) {
            const stores = item.bindStores;
            return (
                <Box style={style}>
                    {
                        stores.map((store)=>{
                            return (
                                <span key={store.storeId}> ..{store.storeName}</span>
                            )
                        })
                    }
                </Box>
            );
        }
        return (
            <Box style={style}>
                <StorefrontIcon
                    fontSize={AppWord.SMALL} color={AppStyle.mui.disabled}
                    style={{marginRight: 5}}
                />
                {item ? '동적 점포 없음' : <span style={{color:'#ccc'}}>동적: *******</span>}
            </Box>
        )
    };

    if(!item) {
        return(
            <NoDataBox
                title="날짜와 점포를 선택한 후 조회 버튼을 클릭하세요"
            />
        );
    }

    return(
        <Box>
            <Grid2 container spacing={1}>
                <Grid2 item size={12} style={{textAlign: 'right'}}>
                    {
                        item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued ?
                            <Chip
                                label="TSR 발행됨"
                                onClick={()=> onRequestViewDetail(item)}
                                color={AppStyle.mui.primary}
                           />
                            :
                            <Chip
                                label="TSR 발행되지 않음"
                                color={AppStyle.mui.disabled}
                            />
                    }
                </Grid2>
                <Pair title="배차구분" value={item.transTypeCd} />
                <Pair title="차종" value={item.vehTypeName} />
                <Pair title="운송사" value={item.comName} />
                <Pair title="접안시간" value={getHhmm(item.shprReqTime)} />
                <Pair title="출발요청시간" value={getHhmm(item.dptrReqTime)} />
                <Pair title="도착요청시간" value={getHhmm(item.arrReqTime)} />
                <Pair title="Dock No." value={item.dockno || '-'} />
                <Pair title="추가운임" value={item.addCharge || '-'} />
                <Pair title="비고" value={item.note || <span>&nbsp;</span>} style={{border:0}} />
                <Grid2 item size={12}>
                    {renderBindStores()}
                </Grid2>
                <Grid2 item size={12}
                    style={{backgroundColor:'#ccf2ff', borderRadius:'0.5rem', }}
                >
                    <Box style={{display:'flex', alignItems:'center'}}>
                        <LabelBox
                        >
                            {item.vehPlates}
                        </LabelBox>
                        <LabelBox>
                            <PersonIcon fontSize={AppWord.SMALL} color={AppStyle.mui.info} />
                            {
                                item.driverName || <span style={{color:'#ccc'}}>[없음]</span>
                            }
                        </LabelBox>
                        <LabelBox>
                            <SystemSecurityUpdateGoodIcon fontSize={AppWord.SMALL} color={AppStyle.mui.info} />
                            {
                                item.driverMobile || <span style={{color:'#ccc'}}>[연락처 없음]</span>
                            }
                        </LabelBox>
                    </Box>
                </Grid2>
                <Grid2 item size={12}>
                    <Button
                        variant={AppStyle.mui.contained} fullWidth
                        size={AppWord.SMALL}
                        color={AppStyle.mui.inherit}
                        disabled={!canSendReadyMsg || !goodMobile || daysOld > 1 || loadingAny}
                        onClick={onClickSendReadyMsg}
                    >출차 준비 메시지</Button>
                    <Button
                        variant={AppStyle.mui.contained} fullWidth
                        size={AppWord.SMALL}
                        color={AppStyle.mui.inherit}
                        disabled={loadingAny}
                        style={{marginTop: '0.5rem'}}
                        onClick={onClickSetDetail}
                    >상차 상세내역 및 물류집기</Button>
                </Grid2>
                <Grid2 item size={6}>
                    <Button
                        size={AppWord.SMALL}
                        variant={AppStyle.mui.contained} fullWidth
                        color={AppStyle.mui.primary}
                        disabled={!canPrintTsr || loadingAny}
                        style={{marginTop: '0.2rem'}}
                        onClick={onClickPrintTsr}
                    >
                        TSR
                    </Button>
                </Grid2>
                <Grid2 item size={6}>
                    <Button
                        variant={AppStyle.mui.contained} fullWidth
                        size={AppWord.SMALL}
                        color={AppStyle.mui.secondary}
                        disabled={!canSendMessageToLeave || loadingAny}
                        style={{marginTop: '0.2rem'}}
                        onClick={onClickSendMessageForDeparture}
                    >메시지</Button>
                </Grid2>
            </Grid2>
            <TransPlanTsrEditorGroup
                    open={editorState.open}
                    item={editorState.data}
                    tabSelected={editorState.tab}
                    onClose={onRequestCloseGroupEditor}
                    onRequestSave={onRequestSaveTsr}
            />
        </Box>
    );
}
